import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { radius } from '@libertify/ui';
import { ChatBot } from './ChatBot';
import type { MediaProjectInstanceResponse } from '../../types';

export type MobileChatFloatProps = {
  publication: MediaProjectInstanceResponse;
  documentIds: string[];
  organizationId: string;
  openChat: boolean;
  firstQuery?: string;
  suggestions?: string[];
  setSuggestions: (s: string[]) => void;
  hidden?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  isLegacy?: boolean;
};

const SPARKLE_SVG = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 256 256"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M208,144a15.78,15.78,0,0,1-10.42,14.94l-51.65,19-19,51.61a15.92,15.92,0,0,1-29.88,0L78.07,178l-51.65-19a15.92,15.92,0,0,1,0-29.88l51.65-19,19-51.65a15.92,15.92,0,0,1,29.88,0l19,51.65,51.65,19A15.78,15.78,0,0,1,208,144ZM152,48h16V64a8,8,0,0,0,16,0V48h16a8,8,0,0,0,0-16H184V16a8,8,0,0,0-16,0V32H152a8,8,0,0,0,0,16Zm88,32h-8V72a8,8,0,0,0-16,0v8h-8a8,8,0,0,0,0,16h8v8a8,8,0,0,0,16,0V96h8a8,8,0,0,0,0-16Z"
      fill="#14B8A6"
    />
  </svg>
);

const MIN_HEIGHT = 160;
const MAX_HEIGHT_VH = 75;
const DEFAULT_HEIGHT = 280;

export const MobileChatFloat = ({
  publication,
  documentIds,
  organizationId,
  openChat,
  firstQuery,
  suggestions,
  setSuggestions,
  hidden = false,
  expanded: controlledExpanded,
  onExpandedChange,
  isLegacy,
}: MobileChatFloatProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded ?? internalExpanded;
  const setExpanded = useCallback(
    (val: boolean) => {
      setInternalExpanded(val);
      onExpandedChange?.(val);
    },
    [onExpandedChange],
  );
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);

  // Open expanded when firstQuery arrives
  useEffect(() => {
    if (firstQuery) {
      setExpanded(true);
    }
  }, [firstQuery]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      dragRef.current = { startY: touch.clientY, startHeight: height };
    },
    [height],
  );

  useEffect(() => {
    const onTouchMove = (e: TouchEvent) => {
      if (!dragRef.current) return;
      const touch = e.touches[0];
      const delta = dragRef.current.startY - touch.clientY;
      const maxHeight = window.innerHeight * (MAX_HEIGHT_VH / 100);
      const newHeight = Math.min(
        maxHeight,
        Math.max(MIN_HEIGHT, dragRef.current.startHeight + delta),
      );
      setHeight(newHeight);
    };

    const onTouchEnd = () => {
      dragRef.current = null;
    };

    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  if (!expanded) {
    return (
      <CollapsedBar
        $hidden={hidden}
        className="mobile-chat-float mobile-chat-float--collapsed"
        onClick={() => setExpanded(true)}
      >
        {SPARKLE_SVG}
        <Placeholder>{t('askMeAnything', 'Ask a question...')}</Placeholder>
        <AskPill>{t('ask', 'Ask')}</AskPill>
      </CollapsedBar>
    );
  }

  return (
    <ExpandedCard
      $hidden={hidden}
      $height={height}
      className={`mobile-chat-float ${isLegacy ? 'mobile-chat-float--expanded-legacy' : 'mobile-chat-float--expanded'}`}
    >
      <DragHandle
        onTouchStart={handleTouchStart}
        style={{ touchAction: 'none' }}
      >
        <DragPill />
      </DragHandle>
      <Header>
        <HeaderLeft>
          {SPARKLE_SVG}
          <HeaderTitle>
            {t('askAboutVideo', 'Ask about this video')}
          </HeaderTitle>
        </HeaderLeft>
        <CloseBtn onClick={() => setExpanded(false)} aria-label="Close chat">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </CloseBtn>
      </Header>
      <ChatBody className="mobile-chat-float__body">
        <ChatBot
          publication={publication}
          open={openChat || expanded}
          documentIds={documentIds}
          organizationId={organizationId}
          firstQuery={firstQuery}
          suggestions={suggestions}
          setSuggestions={setSuggestions}
          isLegacy={isLegacy}
        />
      </ChatBody>
    </ExpandedCard>
  );
};

const CollapsedBar = styled.div<{ $hidden: boolean }>`
  position: absolute;
  bottom: 32px;
  left: 24px;
  right: 24px;
  z-index: 20;
  background: white;
  border-radius: ${radius.box}px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  pointer-events: ${(p) => (p.$hidden ? 'none' : 'auto')};
  transition: opacity 0.3s ease-out;
`;

const Placeholder = styled.span`
  flex: 1;
  font-size: 14px;
  color: #9ca3af;
`;

const AskPill = styled.span`
  background: #14b8a6;
  color: white;
  border-radius: ${radius.pill}px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
`;

const ExpandedCard = styled.div<{ $hidden: boolean; $height: number }>`
  position: absolute;
  bottom: 32px;
  left: 24px;
  right: 24px;
  z-index: 20;
  height: ${(p) => p.$height}px;
  background: white;
  border-radius: ${radius.box}px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  pointer-events: ${(p) => (p.$hidden ? 'none' : 'auto')};
  transition: opacity 0.3s ease-out;
`;

const DragHandle = styled.div`
  flex-shrink: 0;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ns-resize;
  user-select: none;
`;

const DragPill = styled.div`
  width: 32px;
  height: 4px;
  border-radius: ${radius.pill}px;
  background: #d1d5db;
`;

const Header = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  color: #9ca3af;
  border-radius: 6px;

  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const ChatBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
